import { useQuery } from '@apollo/client/react'

import { graphql } from '~publish/graphql'
import { canPerform } from '~publish/utils/permissions/canPerform'

/**
 * Lean lookup of the duplicate-eligibility signals for a repost takeaway's post.
 * Mirrors the gate the Performance-per-Post table uses: a post can only be
 * duplicated when its `allowedActions` includes `duplicatePost` (native posts
 * not shared via Buffer are excluded server-side via `post.via`). We also pull
 * `externalLink` so the card can offer a "View Post" fallback when the post
 * can't be duplicated.
 *
 * Intentionally selects only the fields needed rather than the heavier
 * `PostCard_Post` fragment used by `GetPost`.
 */
const RepostPostEligibilityDocument = graphql(/* GraphQL */ `
  query RepostPostEligibility($id: PostId!) {
    post(input: { id: $id }) {
      id
      allowedActions
      externalLink
    }
  }
`)

interface RepostPostEligibility {
  canDuplicate: boolean
  canViewPost: boolean
  externalLink: string | null | undefined
  loading: boolean
}

export function useRepostPostEligibility(
  postId: string | null,
): RepostPostEligibility {
  const { data, loading } = useQuery(RepostPostEligibilityDocument, {
    variables: { id: postId ?? '' },
    skip: !postId,
    fetchPolicy: 'cache-first',
  })

  const post = data?.post

  return {
    canDuplicate: canPerform(post, 'duplicatePost'),
    canViewPost: !!post?.externalLink && canPerform(post, 'viewPost'),
    externalLink: post?.externalLink,
    loading,
  }
}
